/**
 * Shared Types
 * Centralized type definitions following TypeScript best practices
 */

// === Base Types ===
export interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt: string;
}

// === User Types ===
export interface User {
  id?: string; // Added for compatibility
  username: string;
  email: string;
  project_id: string;
  is_active: boolean;
  first_name?: string;
  last_name?: string;
  data: Record<string, unknown>;
  is_admin?: boolean; // Only included when true
  is_god?: boolean; // Only included when true
  created_at?: string;
  updated_at?: string;
  avatar?: string; // Added for Navigation component compatibility
}

export interface UserProfile {
  bio?: string;
  website?: string;
  location?: string;
  company?: string;
  socialLinks?: SocialLinks;
}

export interface SocialLinks {
  twitter?: string;
  linkedin?: string;
  github?: string;
  website?: string;
}

export enum UserRole {
  ADMIN = 'admin',
  USER = 'user',
  MODERATOR = 'moderator',
}

// === User Settings Types ===
export interface UserSettings {
  theme: 'light' | 'dark' | 'system';
  notifications: {
    email: boolean;
    push: boolean;
    comment_replies: boolean;
  };
  privacy: {
    profile_public: boolean;
    show_activity: boolean;
  };
}

// === User Activity Types ===
export interface UserActivity {
  id: number;
  activity_type: string;
  description: string;
  created_at: string;
  ip_address: string;
  user_agent: string;
}

// === User Metadata Types ===
export interface UserMetadata {
  onboarding_completed: boolean;
  preferences: {
    dashboard_layout: 'grid' | 'list';
    auto_save: boolean;
  };
  subscription?: {
    plan: string;
    expires: string;
  };
  custom_fields?: Record<string, unknown>;
}

// === Blog Types ===
export interface Blog extends BaseEntity {
  title: string;
  content: string;
  excerpt?: string;
  slug: string;
  status: BlogStatus;
  tags: string[];
  author: Author;
  publishedAt?: string;
  featuredImage?: string;
  readingTime?: number;
  views?: number;
  likes?: number;
}

export interface Author {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

export enum BlogStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  ARCHIVED = 'archived',
}

// === Project Types ===
export interface Project extends BaseEntity {
  title: string;
  description: string;
  longDescription?: string;
  image?: string;
  tags: string[];
  status?: ProjectStatus;
  client?: string;
  startDate?: string;
  completionDate?: string;
  githubUrl?: string;
  liveUrl?: string;
  link?: string;
  featured: boolean;
  technologies?: string[];
  category?: ProjectCategory;
  details?: {
    overview: string[];
    solution: string[];
    results: string[];
  };
}

export enum ProjectStatus {
  LIVE = 'live',
  INACTIVE = 'inactive',
  PLANNING = 'planning',
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  ON_HOLD = 'on_hold',
  CANCELLED = 'cancelled',
}

export enum ProjectCategory {
  WEB_APP = 'web_app',
  MOBILE_APP = 'mobile_app',
  DESKTOP_APP = 'desktop_app',
  API = 'api',
  WEBSITE = 'website',
  OTHER = 'other',
}

// === API Types ===
export interface ApiResponse<T = unknown> {
  data: T;
  success: boolean;
  message?: string;
  error?: ApiError;
  meta?: ApiMeta;
}

export interface ApiError {
  type?: string; // Error type (e.g., 'validation_error', 'authentication_error')
  message?: string; // Human-readable error message
  status_code?: number; // HTTP status code
  details?: Record<string, unknown>; // Additional error details
}

export interface ApiMeta {
  page?: number;
  limit?: number;
  total?: number;
  totalPages?: number;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  meta: Required<ApiMeta>;
}

// === Backend Response Types (Raw API responses) ===
export interface BackendUser {
  username: string;
  email: string;
  project_id: string;
  is_active: boolean;
  is_admin?: boolean;
  is_god?: boolean;
  created_at: string;
  updated_at: string;
  data: Record<string, unknown>;
}

export interface BackendBlog {
  blog_id: string;
  project_id: string;
  title: string;
  content: string;
  author_id: string;
  tags: string;
  status: 'DRAFT' | 'PUBLISHED';
  comments: Array<{
    user_id: string;
    content: string;
    timestamp: string;
  }>;
  likes: number;
  created_at: string;
  updated_at: string;
}

// === Request Types ===
export interface LoginRequest {
  email?: string; // Either email OR username is required
  username?: string; // Either email OR username is required  
  password: string;
}

export interface RegisterRequest {
  username: string; // Required, min 3 characters
  email: string; // Required, must contain @ and .
  password: string; // Required, min 6 characters
  first_name?: string; // Optional
  last_name?: string; // Optional
  bio?: string; // Optional
  profile_image_url?: string; // Optional
  metadata?: Record<string, unknown>; // Optional key-value data
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string; // Must be min 6 characters
}

export interface CreateBlogRequest {
  title: string; // Required
  content: string; // Required
  excerpt?: string; // Optional
  featured_image_url?: string; // Optional
  is_published?: boolean; // Optional, defaults to false
  is_featured?: boolean; // Optional, defaults to false
  tags?: string[]; // Optional array of tags
}

export interface UpdateBlogRequest {
  blog_id: string; // Required - blog ID to update
  title?: string; // Optional
  content?: string; // Optional
  excerpt?: string; // Optional
  featured_image_url?: string; // Optional
  is_published?: boolean; // Optional
  is_featured?: boolean; // Optional
  tags?: string[]; // Optional
}

export interface PublishBlogRequest {
  blog_id: string; // Required
  is_published: boolean; // Required
}

export interface AutosaveBlogRequest {
  blog_id: string; // Required
  title?: string; // Optional
  content?: string; // Optional
}

export interface BlogFilters {
  search?: string; // Search query
  limit?: number; // Number of results per page
  offset?: number; // Pagination offset
  category?: string; // Blog category filter
  is_published?: boolean; // Filter by published status
  author_id?: string; // Filter by author
  tags?: string; // Comma-separated tags
  date_from?: string; // ISO date string
  date_to?: string; // ISO date string
  sort_by?: 'created_at' | 'title' | 'updated_at' | 'view_count';
  sort_order?: 'asc' | 'desc';
}

export interface ProjectFilters {
  search?: string; // Search query
  limit?: number; // Number of results per page
  offset?: number; // Pagination offset
  status?: ProjectStatus;
  category?: ProjectCategory;
  tags?: string[];
  featured?: boolean;
  client?: string;
  sort_by?: 'created_at' | 'title' | 'completion_date';
  sort_order?: 'asc' | 'desc';
}

export interface UserActivityFilters {
  limit?: number; // Number of results per page
  offset?: number; // Pagination offset
  activity_type?: string; // Filter by activity type
  date_from?: string; // ISO date string
  date_to?: string; // ISO date string
}

export interface AdminUserFilters {
  search?: string; // Search query
  status?: 'active' | 'inactive'; // User status
  role?: string; // User role
  sort_by?: 'created_at' | 'username' | 'email';
  sort_order?: 'asc' | 'desc';
  limit?: number; // Number of results per page
  offset?: number; // Pagination offset
}

// === Response Types ===
export interface AuthResponse {
  success: boolean;
  message: string;
  data?: {
    token: string;
    user_id: string;
    expires_at: string;
  };
  error?: ApiError;
  timestamp?: string;
}

export interface UserProfileResponse {
  success: boolean;
  message: string;
  data?: {
    username: string;
    email: string;
    project_id: string;
    is_active: boolean;
    is_admin?: boolean;
    is_god?: boolean;
    created_at: string;
    updated_at: string;
    data: Record<string, unknown>;
    first_name?: string;
    last_name?: string;
    bio?: string;
    avatar_url?: string;
    phone?: string;
    timezone?: string;
    language?: string;
  };
  error?: ApiError;
}

export interface BlogResponse {
  success: boolean;
  message: string;
  data?: {
    id: string;
    title: string;
    content: string;
    excerpt?: string;
    author: { username: string; name: string };
    created_at: string;
    updated_at: string;
    view_count: number;
    is_published: boolean;
    featured_image_url?: string;
    tags?: string[];
  };
  error?: ApiError;
}

export interface BlogListResponse {
  success: boolean;
  message: string;
  data?: {
    blogs: Array<{
      id: string;
      title: string;
      content: string;
      excerpt?: string;
      author: { username: string; name: string };
      created_at: string;
      updated_at: string;
      view_count: number;
      is_published: boolean;
      featured_image_url?: string;
      tags?: string[];
    }>;
    pagination: {
      page: number;
      per_page: number;
      total: number;
      total_pages: number;
    };
  };
  error?: ApiError;
}

export interface CreateBlogResponse {
  success: boolean;
  message: string;
  data?: {
    blog_id: string;
    title: string;
    is_published: boolean;
  };
  error?: ApiError;
}

export interface CommentResponse {
  success: boolean;
  message: string;
  data?: {
    comment_id: string;
    created_at: string;
  };
  error?: ApiError;
  timestamp?: string;
}

export interface LikeResponse {
  success: boolean;
  message: string;
  data?: {
    action: 'liked' | 'unliked';
    total_likes: number;
  };
  error?: ApiError;
  timestamp?: string;
}

export interface FileUploadResponse {
  success: boolean;
  message: string;
  data?: {
    file_url: string;
    file_name: string;
    file_size: number;
  };
  error?: ApiError;
  timestamp?: string;
}

// === Backend API Response Types ===
export interface BackendLoginResponse {
  message: string;
  token: string;
}

export interface BackendRegisterResponse {
  message: string;
  username: string;
}

export interface BackendCreateBlogResponse {
  message: string;
  blog_id: string;
}

export interface BackendUpdateBlogResponse {
  message: string;
  blog_id: string;
}

export interface BackendDeleteBlogResponse {
  message: string;
}

export interface BackendPublishBlogResponse {
  message: string;
  blog_id: boolean; // Note: Backend returns boolean, not string
}

export interface BackendUserResponse {
  users?: BackendUser[]; // For fetchall endpoint
  username?: string;
  email?: string;
  project_id?: string;
  is_active?: boolean;
  is_admin?: boolean;
  is_god?: boolean;
  created_at?: string;
  updated_at?: string;
  data?: Record<string, unknown>;
}

export interface BackendErrorResponse {
  message?: string;
  error?: string;
  reason?: string | null;
  errors?: Record<string, string[]>; // For validation errors
}

// === UI Component Types ===
export interface LoadingState {
  isLoading: boolean;
  error?: string | null;
  success?: boolean;
}

export interface FormFieldProps {
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  helperText?: string;
}

export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  onClick?: () => void;
}

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  children: React.ReactNode;
}

// === Utility Types ===
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export type Optional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

export type RequiredFields<T, K extends keyof T> = T & Required<Pick<T, K>>;

// === Event Types ===
export interface CustomEvent<T = unknown> {
  type: string;
  payload: T;
  timestamp: number;
}

// === Error Types ===
export interface AppError {
  code: string;
  message: string;
  details?: unknown;
  timestamp: number;
  userId?: string;
}

// === Logging Types ===
export enum LogLevel {
  DEBUG = 'debug',
  INFO = 'info',
  WARN = 'warn',
  ERROR = 'error',
}

export interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: number;
  category?: string;
  userId?: string;
  metadata?: Record<string, unknown>;
}

// === User Request Types ===
export interface UpdateUserRequest {
  email?: string;
  first_name?: string;
  last_name?: string;
  bio?: string;
  profile_image_url?: string;
  phone?: string;
  timezone?: string;
  language?: string;
}

export interface UpdateUserSettingsRequest {
  email_preferences?: {
    newsletter?: boolean;
    blog_notifications?: boolean;
  };
  privacy_settings?: {
    profile_visibility?: 'public' | 'private';
    show_email?: boolean;
  };
  notification_settings?: {
    push_enabled?: boolean;
    email_enabled?: boolean;
  };
}

export interface UpdateUserMetadataRequest {
  preferences?: Record<string, unknown>;
  custom_data?: Record<string, unknown>;
}

export interface CreateCommentRequest {
  content: string; // Required
}

export interface FileUploadRequest {
  file: File; // The file to upload
  upload_type: 'avatar' | 'blog' | 'content'; // Required
  alt_text?: string; // Optional image description
}

export interface CreateProjectRequest {
  name: string; // Required
  description: string; // Required
  contact_email: string; // Required
  subscription_type: 'free' | 'premium' | 'enterprise'; // Required
}

// === Query Parameters Types ===
export interface BlogQueryParams {
  id?: string; // For fetching single blog
  search?: string; // Search query
  limit?: number; // Results per page
  offset?: number; // Pagination offset
}

export interface TagSuggestionsParams {
  q: string; // Query string for tag suggestions (required)
}

export interface DeleteBlogParams {
  id: string; // Blog ID to delete (required)
}

export interface OAuthCallbackParams {
  code: string; // OAuth authorization code
  state: string; // OAuth state parameter
}

// === User Configuration Types ===
export interface UserConfig {
  autoRedirect: boolean;
  redirectPaths: {
    login: string;
    logout: string;
    dashboard: string;
  };
  tokenStorageKey: string;
  userStorageKey: string;
}

// === Admin Types ===
export * from './admin';
